import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { getFirebaseConfig } from './config';
import { calculateSign } from './auth-utils';
import type { Game } from '../hooks/useGames';
import log from '../utils/logger';

// Tipi per i parametri di ricerca
interface SearchDataParams {
    type?: string;
    search: string;
    accessToken?: string;
    sign?: string;
}

// Tipo completo per la function di ricerca
interface SearchDataRequest extends SearchDataParams {
    model: string;
    version: string;
    prodID: string;
    tf: string;
    lan: string;
    deviceID: string;
    requestId: string;
}

// Callable function per la ricerca
export const getSearchData = httpsCallable<SearchDataRequest, { result?: { status: number; games: Game[] }; status?: number; games?: Game[] }>(
    functions,
    'search-searchData'
);

// Funzione helper per la ricerca
export const fetchSearchData = async (searchQuery: string, user?: { getIdToken: () => Promise<string> }): Promise<Game[]> => {
    // Ottieni il token se l'utente è autenticato
    let accessToken = "";
    if (user) {
        try {
            accessToken = await user.getIdToken();
        } catch (error) {
            log.error('Errore nel recupero del token:', error);
        }
    }

    // Calcola requestId e sign usando il metodo Swift
    const { requestId, sign } = await calculateSign();

    const requestParams: SearchDataRequest = {
        ...getFirebaseConfig(),
        type: "all",
        search: searchQuery,
        accessToken: accessToken,
        sign: sign,
        requestId: requestId,
    };

    try {
        log.debug('🔍 Ricerca in corso per:', searchQuery);
        log.debug('📤 Parametri richiesta:', requestParams);
        
        const result = await getSearchData(requestParams);
        log.debug('📥 Risposta ricevuta:', result.data);

        // Determina la struttura della risposta dinamicamente
        let searchData;
        if (result.data.result) {
            // Struttura: { result: { status, games } }
            searchData = result.data.result;
        } else if (result.data.status !== undefined) {
            // Struttura: { status, games }
            searchData = result.data;
        } else {
            throw new Error('Struttura risposta non riconosciuta');
        }

        if (searchData.status === 0) {
            log.debug('✅ Ricerca completata:', searchData.games?.length || 0, 'giochi trovati');
            return searchData.games || [];
        } else {
            throw new Error(`Errore nella ricerca: status ${searchData.status}`);
        }
    } catch (error) {
        log.error('❌ Error fetching search data:', error);
        throw error;
    }
};